import cx from 'classnames';
import * as _ from 'lodash';
import { useMemo, type VoidFunctionComponent } from 'react';
import { useDispatch } from 'react-redux';

import { RevisionLicenseConclusionScope } from '../../../../../../../shared/model-typings/revisionlicenseconclusion';
import { useDeleteLicense } from '../../../../../../hooks/api/correctionsV2/useDeleteLicense';
import * as DependenciesV2Store from '../../../../../../redux/dependenciesV2';
import Button from '../../../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../../../generic/Button/button-constants';

import useConcludeLicense, { type ChooseParams } from './hooks/useConcludeLicense';
import useUnconcludeLicense from './hooks/useUnconcludeLicense';
import type { LicenseTabScope } from './types';

import styles from './DependencyLicenses.module.less';

type Props = {
  licenseSignature: string;
  revisionLicenseId: number;
  scope: LicenseTabScope;
  isConcluded?: boolean;
  onView: () => void;
  canEditLicenses: boolean;
  isLicenseConcludedEnabled: boolean;
};

export const LicenseActionButtons: VoidFunctionComponent<Props> = ({
  licenseSignature,
  revisionLicenseId,
  scope,
  isConcluded,
  onView,
  canEditLicenses,
  isLicenseConcludedEnabled,
}) => {
  const dispatch = useDispatch();
  const chooseParams: ChooseParams | null = useMemo(() => {
    if (scope.type === 'project') {
      return {
        dependencyRevisionLocator: scope.dependencyRevisionLocator,
        scope: {
          scope: RevisionLicenseConclusionScope.PROJECT,
          projectLocator: scope.projectLocator,
        },
        originId: scope.revisionLocator,
      };
    }

    if (scope.type === 'releaseGroup') {
      return {
        dependencyRevisionLocator: scope.dependencyRevisionLocator,
        scope: {
          scope: RevisionLicenseConclusionScope.RELEASE_GROUP,
          releaseGroupId: scope.releaseGroupId,
        },
        originId: scope.releaseId.toString(),
      };
    }

    return null;
  }, [scope]);

  const { mutateAsync: deleteLicense, isPending: isDeletingLicense } = useDeleteLicense({
    dependencyRevisionLocator: scope.dependencyRevisionLocator,
    revisionLicenseId,
  });
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { mutateAsync: concludeLicense } = chooseParams ? useConcludeLicense(chooseParams) : { mutateAsync: _.noop };
  const { mutateAsync: unconcludeLicense } = chooseParams
    // eslint-disable-next-line react-hooks/rules-of-hooks
    ? useUnconcludeLicense(chooseParams)
    : { mutateAsync: _.noop };

  const onConcludeLicense = async (chosenLicense: string, action: 'conclude' | 'unconclude') => {
    if (chooseParams) {
      if (action === 'conclude') {
        await concludeLicense({
          chosenLicense,
          chooseParams,
        });
      } else {
        await unconcludeLicense({
          chosenLicense,
          chooseParams,
        });
      }
      dispatch(DependenciesV2Store.actions.preFetchLoading());
      dispatch(DependenciesV2Store.actions.fetchDependencies());
    }
  };

  const onConclude = () => canEditLicenses ? onConcludeLicense(licenseSignature, isConcluded ? 'unconclude' : 'conclude') : _.noop();

  return (
    <div className={styles.actionButtonsContainer}>
      <Button size={BUTTON_SIZE.SMALL} type={BUTTON_TYPE.SECONDARY} onClick={() => onView()}>
        <span className={styles.actionButtonText}>View</span>
      </Button>
      {scope.type !== 'global' && canEditLicenses && (
        <Button
          size={BUTTON_SIZE.SMALL}
          type={BUTTON_TYPE.SECONDARY}
          isDisabled={!canEditLicenses || isDeletingLicense}
          onClick={async () => {
            await deleteLicense();
            dispatch(DependenciesV2Store.actions.preFetchLoading());
            dispatch(DependenciesV2Store.actions.fetchDependencies());
          }}
        >
          <span className={styles.actionButtonText}>Delete</span>
        </Button>
      )}
      {scope.type !== 'global' && onConclude && canEditLicenses && isLicenseConcludedEnabled && (
        <Button
          size={BUTTON_SIZE.SMALL}
          type={isConcluded ? BUTTON_TYPE.ERROR : BUTTON_TYPE.SECONDARY}
          isDisabled={!canEditLicenses}
          onClick={onConclude}
        >
          <span className={cx(styles.actionButtonText, !isConcluded && styles.unConcludedActionButtonText)}>
            {isConcluded ? 'Unconclude' : 'Conclude'}
          </span>
        </Button>
      )}
    </div>
  );
};
